import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import { useLocation } from 'react-router-dom';
import Admin from '../components/Admin';
import Profile from '../components/Profile';

const Settings = ({ 
  refreshInterval, 
  rssFeeds,
  userSettings,
  userPrompts,
  userApiKeys,
  user
}) => {
  useAuth();
  const location = useLocation();
  const { 
    updateUserSettings,
    addUserPrompt,
    updateUserPrompt,
    deleteUserPrompt,
    addUserFeed,
    updateUserFeed,
    deleteUserFeed,
    updateUserApiKeys,
    resetToDefaults
  } = useUserData();
  const [activeTab, setActiveTab] = useState('integrations');
  const [activeModule, setActiveModule] = useState(null);
  const [activeSocialPlatform, setActiveSocialPlatform] = useState('reddit');
  const [newFeedName, setNewFeedName] = useState('');
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptText, setNewPromptText] = useState('');

  // Default OpenAI key for Managed subscription users
  const defaultManagedKey = process.env.REACT_APP_OPENAI_API_KEY || "";

  // Handle URL parameters to set active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab && ['integrations', 'feeds', 'ai-prompts', 'profile', 'admin'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const handleRefreshIntervalChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 10 && value <= 300) {
      updateUserSettings({ refreshInterval: value });
    }
  };


  const handleAddFeed = async () => {
    if (newFeedName.trim() && newFeedUrl.trim()) {
      const newFeed = {
        name: newFeedName.trim(),
        url: newFeedUrl.trim(),
        enabled: true,
        order: (rssFeeds?.length || 0) + 1
      };
      await addUserFeed(newFeed);
      setNewFeedName('');
      setNewFeedUrl('');
    }
  };

  const handleRemoveFeed = async (feedId) => {
    await deleteUserFeed(feedId);
  };

  const handleToggleFeed = async (feedId) => {
    const feed = rssFeeds.find(f => f.id === feedId);
    if (feed) {
      await updateUserFeed(feedId, { enabled: !feed.enabled });
    }
  };

  const handleEditFeed = async (feedId, field, value) => {
    await updateUserFeed(feedId, { [field]: value });
  };

  const handleColorChange = async (feedId, color) => {
    await updateUserFeed(feedId, { color: color });
  };

  const handleAddPrompt = async () => {
    if (newPromptName.trim() && newPromptText.trim()) {
      const newPrompt = {
        name: newPromptName.trim(),
        prompt: newPromptText.trim(),
        order: (userPrompts?.length || 0) + 1
      };
      await addUserPrompt(newPrompt);
      setNewPromptName('');
      setNewPromptText('');
    }
  };

  const handleRemovePrompt = async (promptId) => {
    await deleteUserPrompt(promptId);
  };

  const handleEditPrompt = async (promptId, field, value) => {
    await updateUserPrompt(promptId, { [field]: value });
  };

  // API Key handlers
  const handleOpenAIKeyChange = async (value) => {
    if (user?.subscriptionType !== 'MANAGED' && user?.role !== 'ADMIN') {
      await updateUserApiKeys({ openaiApiKey: value });
    }
  };

  const handleFinnhubKeyChange = async (value) => {
    if (user?.subscriptionType !== 'MANAGED' && user?.role !== 'ADMIN') {
      await updateUserApiKeys({ finnhubApiKey: value });
    }
  };

  const handleFinnhubEnabledChange = async (enabled) => {
    await updateUserApiKeys({ finnhubEnabled: enabled });
  };

  const handleYahooFinanceEnabledChange = async (enabled) => {
    console.log('Settings: handleYahooFinanceEnabledChange called with:', enabled);
    console.log('Settings: Current userApiKeys:', userApiKeys);
    await updateUserApiKeys({ yahooFinanceEnabled: enabled });
  };

  const handleRedditEnabledChange = async (enabled) => {
    await updateUserApiKeys({ redditEnabled: enabled });
  };

  const handleRedditApiKeyChange = async (value) => {
    await updateUserApiKeys({ redditApiKey: value });
  };

  // Reset to defaults handler
  const handleResetToDefaults = async () => {
    if (window.confirm('Are you sure you want to reset all prompts and feeds to defaults? This will remove all your custom prompts and feeds.')) {
      const success = await resetToDefaults();
      if (success) {
        alert('Successfully reset to defaults!');
      } else {
        alert('Failed to reset to defaults. Please try again.');
      }
    }
  };

  const tabs = [
    { id: 'integrations', label: 'Integrations & Modules', icon: '🔗' },
    { id: 'ai-prompts', label: 'AI Prompts', icon: '🤖' },
    { id: 'feeds', label: 'Feeds', icon: '📰' },
    { id: 'other-settings', label: 'Other Settings', icon: '⚙️' },
    { id: 'profile', label: 'Profile', icon: '👤' },
    ...(user?.role === 'ADMIN' ? [{ id: 'admin', label: 'Admin', icon: '👑' }] : [])
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'integrations':
        if (activeModule === 'openai') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Integrations
                </button>
                <div className="module-title">
                  <div className="module-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="#dc2626" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h2>OpenAI Module</h2>
                    <p>Configure AI-powered analysis using ChatGPT</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                <div className="setting-item">
                  <label htmlFor="openai-key">API Key</label>
                  <div className="setting-control">
                    <input
                      type="password"
                      id="openai-key"
                      value={user?.subscriptionType === 'MANAGED' ? defaultManagedKey : userApiKeys.openaiApiKey}
                      onChange={(e) => handleOpenAIKeyChange(e.target.value)}
                      placeholder="sk-..."
                      className="setting-input api-key-input"
                      disabled={user?.subscriptionType === 'MANAGED'}
                    />
                    {user?.subscriptionType === 'MANAGED' && (
                      <span className="managed-badge">Managed</span>
                    )}
                  </div>
                  <p className="setting-description">
                    {user?.subscriptionType === 'MANAGED' 
                      ? 'Using our managed OpenAI API key for seamless access.'
                      : 'Your OpenAI API key for ChatGPT integration.'
                    }
                  </p>
                </div>
                
                <div className="module-actions">
                  <button 
                    onClick={() => {
                      alert('OpenAI configuration saved successfully!');
                    }}
                    className="setting-button primary"
                    disabled={user?.subscriptionType === 'MANAGED'}
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          );
        }
        
        if (activeModule === 'finnhub') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Integrations
                </button>
                <div className="module-title">
                  <div className="module-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h2>FinHub Module</h2>
                    <p>Configure real-time stock prices and market data</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                <div className="setting-item">
                  <label>
                    <input 
                      type="checkbox" 
                      className="setting-checkbox" 
                      checked={userApiKeys?.finnhubEnabled === true}
                      onChange={(e) => handleFinnhubEnabledChange(e.target.checked)}
                    />
                    Enable FinHub Integration
                  </label>
                  <p className="setting-description">
                    Fetch real-time stock prices and company data from FinHub API. 
                    <strong>Note:</strong> Only one finance module can be enabled at a time. Enabling FinHub will disable Yahoo Finance.
                  </p>
                </div>

                {userApiKeys?.finnhubEnabled !== false && (
                  <div className="setting-item">
                    <label htmlFor="finnhub-key">API Key</label>
                    <div className="setting-control">
                      <input
                        type="password"
                        id="finnhub-key"
                        value={userApiKeys.finnhubApiKey}
                        onChange={(e) => handleFinnhubKeyChange(e.target.value)}
                        placeholder="Enter your Finnhub API key"
                        className="setting-input api-key-input"
                      />
                    </div>
                    <p className="setting-description">
                      Using BullBearly's FinHub API key for stock data.
                    </p>
                  </div>
                )}
                
                <div className="module-actions">
                  <button 
                    onClick={() => {
                      alert('FinHub configuration saved successfully!');
                    }}
                    className="setting-button primary"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (activeModule === 'yahoo-finance') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Integrations
                </button>
                <div className="module-title">
                  <div className="module-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#7c3aed" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h2>Yahoo Finance 2 Module</h2>
                    <p>Real-time stock data from Yahoo Finance using yahoo-finance2</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                <div className="setting-item">
                  <label>
                    <input 
                      type="checkbox" 
                      className="setting-checkbox" 
                      checked={userApiKeys?.yahooFinanceEnabled === true}
                      onChange={(e) => {
                        console.log('Settings: Yahoo Finance checkbox changed to:', e.target.checked);
                        console.log('Settings: userApiKeys.yahooFinanceEnabled:', userApiKeys?.yahooFinanceEnabled);
                        handleYahooFinanceEnabledChange(e.target.checked);
                      }}
                    />
                    Enable Yahoo Finance 2 Integration
                  </label>
                  <p className="setting-description">
                    Use real-time stock data from Yahoo Finance via yahoo-finance2. 
                    <strong>Note:</strong> Only one finance module can be enabled at a time. Enabling Yahoo Finance will disable FinHub.
                  </p>
                </div>

                <div className="setting-item">
                  <h3>Service Information</h3>
                  <div className="info-box">
                    <p><strong>Features:</strong></p>
                    <ul>
                      <li>Real-time stock quotes</li>
                      <li>Complete OHLC data (Open, High, Low, Close)</li>
                      <li>Volume and market cap</li>
                      <li>PE ratio and dividend yield</li>
                      <li>Company information</li>
                      <li>No API key required</li>
                      <li>High rate limits</li>
                    </ul>
                    
                    <p><strong>Advantages over FinHub:</strong></p>
                    <ul>
                      <li>More comprehensive data</li>
                      <li>No API key required</li>
                      <li>Better rate limits</li>
                      <li>More reliable data source</li>
                    </ul>
                  </div>
                </div>

                <div className="setting-item">
                  <h3>Usage Notes</h3>
                  <div className="info-box">
                    <p>Yahoo Finance 2 is enabled by default and provides comprehensive stock data without requiring an API key. This is the recommended option for most users.</p>
                    <p><strong>Note:</strong> Only one finance module can be active at a time. Choose between Yahoo Finance 2 (recommended) or FinHub based on your needs.</p>
                  </div>
                </div>
                
                <div className="module-actions">
                  <button 
                    onClick={() => {
                      alert('Yahoo Finance 2 configuration saved successfully!');
                    }}
                    className="setting-button primary"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (activeModule === 'reddit') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Integrations
                </button>
                <div className="module-header-content">
                  <div className="module-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="#FF4500" strokeWidth="2" fill="none"/>
                      <circle cx="9" cy="10" r="1.5" fill="#FF4500"/>
                      <circle cx="15" cy="10" r="1.5" fill="#FF4500"/>
                      <path d="M8 14c0 2.2 1.8 4 4 4s4-1.8 4-4" stroke="#FF4500" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h2>Reddit Integration</h2>
                    <p>Social media feeds from Reddit financial discussions</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                <div className="setting-item">
                  <label>
                    <input 
                      type="checkbox" 
                      className="setting-checkbox" 
                      checked={userApiKeys?.redditEnabled === true}
                      onChange={(e) => handleRedditEnabledChange(e.target.checked)}
                    />
                    Enable Reddit Integration
                  </label>
                  <p className="setting-description">
                    Access Reddit financial discussions and social sentiment analysis
                  </p>
                </div>

                {userApiKeys?.redditEnabled === true && (
                  <>
                    <div className="setting-item">
                      <label htmlFor="reddit-api-key">Reddit API Key (Optional)</label>
                      <div className="setting-control">
                        <input
                          type="password"
                          id="reddit-api-key"
                          value={userApiKeys?.redditApiKey || ''}
                          onChange={(e) => handleRedditApiKeyChange(e.target.value)}
                          placeholder="Optional - Leave empty for public access"
                          className="setting-input api-key-input"
                        />
                      </div>
                      <p className="setting-description">
                        <strong>Note:</strong> Reddit API key is optional. Public access allows browsing without authentication.
                        With an API key, you get higher rate limits and access to more features.
                      </p>
                    </div>

                    <div className="setting-item">
                      <h3>Popular Finance Subreddits</h3>
                      <div className="subreddit-suggestions">
                        <span className="subreddit-tag">r/stocks</span>
                        <span className="subreddit-tag">r/investing</span>
                        <span className="subreddit-tag">r/SecurityAnalysis</span>
                        <span className="subreddit-tag">r/ValueInvesting</span>
                        <span className="subreddit-tag">r/StockMarket</span>
                        <span className="subreddit-tag">r/dividends</span>
                        <span className="subreddit-tag">r/options</span>
                        <span className="subreddit-tag">r/wallstreetbets</span>
                      </div>
                      <p className="setting-description">
                        Configure which subreddits to monitor in the Social Feeds section under Feeds tab.
                      </p>
                    </div>

                    <div className="setting-item">
                      <h3>Features</h3>
                      <ul className="feature-list">
                        <li>✅ Browse subreddit posts</li>
                        <li>✅ Global Reddit search</li>
                        <li>✅ Multiple subreddit support</li>
                        <li>✅ Sort by hot, new, top, rising</li>
                        <li>✅ No API key required for basic access</li>
                        <li>✅ Stock detection in posts</li>
                        <li>✅ Sentiment analysis integration</li>
                      </ul>
                    </div>

                    <div className="setting-item">
                      <h3>Rate Limits</h3>
                      <p className="setting-description">
                        • <strong>Without API Key:</strong> 100 requests per minute<br/>
                        • <strong>With API Key:</strong> Higher limits based on Reddit API tier<br/>
                        • <strong>Recommendation:</strong> Start without API key, upgrade if needed
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="settings-tab-content">
            <h2>Integrations & Modules</h2>
            <p className="settings-intro">Configure and manage your third-party integrations and AI modules</p>
            
            {/* AI Modules Section */}
            <div className="module-category">
              <h3 className="category-title">🤖 AI Modules</h3>
              <div className="modules-grid">
                <div 
                  className="module-card clickable"
                  onClick={() => setActiveModule('openai')}
                >
                  <div className="module-header">
                    <div className="module-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="#dc2626" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <div className="module-info">
                      <h3>OpenAI Module</h3>
                      <p>AI-powered analysis using ChatGPT</p>
                      <span className={`module-status ${user?.subscriptionType === 'MANAGED' ? 'managed' : 'custom'}`}>
                        {user?.subscriptionType === 'MANAGED' ? 'Managed' : 'Custom Key'}
                      </span>
                    </div>
                  </div>
                  <div className="module-arrow">→</div>
                </div>
              </div>
            </div>

            {/* Finance Modules Section */}
            <div className="module-category">
              <h3 className="category-title">📊 Finance Modules</h3>
              <div className="modules-grid">
                <div 
                  className="module-card clickable"
                  onClick={() => setActiveModule('finnhub')}
                >
                  <div className="module-header">
                    <div className="module-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <div className="module-info">
                      <h3>FinHub Module</h3>
                      <p>Real-time stock prices and market data</p>
                      <span className={`module-status ${userApiKeys?.finnhubEnabled === true ? 'enabled' : 'disabled'}`}>
                        {userApiKeys?.finnhubEnabled === true ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="module-arrow">→</div>
                </div>

                <div 
                  className="module-card clickable"
                  onClick={() => setActiveModule('yahoo-finance')}
                >
                  <div className="module-header">
                    <div className="module-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#7c3aed" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <div className="module-info">
                      <h3>Yahoo Finance 2 Module</h3>
                      <p>Real-time stock data from Yahoo Finance</p>
                      <span className={`module-status ${userApiKeys?.yahooFinanceEnabled === true ? 'enabled' : 'disabled'}`}>
                        {userApiKeys?.yahooFinanceEnabled === true ? 'Enabled (Default)' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="module-arrow">→</div>
                </div>
              </div>
            </div>

            {/* Social Modules Section */}
            <div className="module-category">
              <h3 className="category-title">📱 Social Modules</h3>
              <div className="modules-grid">
                <div 
                  className="module-card clickable"
                  onClick={() => setActiveModule('reddit')}
                >
                  <div className="module-header">
                    <div className="module-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="#FF4500" strokeWidth="2" fill="none"/>
                        <circle cx="9" cy="10" r="1.5" fill="#FF4500"/>
                        <circle cx="15" cy="10" r="1.5" fill="#FF4500"/>
                        <path d="M8 14c0 2.2 1.8 4 4 4s4-1.8 4-4" stroke="#FF4500" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <div className="module-info">
                      <h3>Reddit Integration</h3>
                      <p>Social media feeds from Reddit discussions</p>
                      <span className={`module-status ${userApiKeys?.redditEnabled === true ? 'enabled' : 'disabled'}`}>
                        {userApiKeys?.redditEnabled === true ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="module-arrow">→</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ai-prompts':
        return (
          <div className="settings-tab-content">
            <h2>AI Prompts Configuration</h2>
            

            <div className="custom-prompts-section">
              <h3>Custom Analysis Prompts</h3>
              <div className="prompts-list">
                {userPrompts.map(prompt => (
                  <div key={prompt.id} className="prompt-item">
                    <div className="prompt-controls">
                      <input
                        type="text"
                        value={prompt.name}
                        onChange={(e) => handleEditPrompt(prompt.id, 'name', e.target.value)}
                        className="prompt-name-input"
                        placeholder="Prompt Name"
                      />
                      <textarea
                        value={prompt.prompt}
                        onChange={(e) => handleEditPrompt(prompt.id, 'prompt', e.target.value)}
                        className="prompt-text-input"
                        placeholder="Enter your custom prompt. Use {SYMBOL} for stock symbol and {COMPANY} for company name."
                        rows="3"
                      />
                      <button
                        onClick={() => handleRemovePrompt(prompt.id)}
                        className="remove-prompt-button"
                        title="Remove this prompt"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="add-prompt-section">
                <h4>Add New Custom Prompt</h4>
                <div className="add-prompt-form">
                  <input
                    type="text"
                    value={newPromptName}
                    onChange={(e) => setNewPromptName(e.target.value)}
                    placeholder="Prompt Name (e.g., Earnings Analysis)"
                    className="setting-input"
                  />
                  <textarea
                    value={newPromptText}
                    onChange={(e) => setNewPromptText(e.target.value)}
                    placeholder="Enter your custom prompt. Use {SYMBOL} for stock symbol and {COMPANY} for company name."
                    className="prompt-textarea"
                    rows="3"
                  />
                  <button
                    onClick={handleAddPrompt}
                    className="add-prompt-button"
                    disabled={!newPromptName.trim() || !newPromptText.trim()}
                  >
                    Add Custom Prompt
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'feeds':
        if (activeModule === 'rss') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Feeds
                </button>
                <div className="module-title">
                  <div className="module-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#dc2626" strokeWidth="2" fill="none"/>
                      <path d="M8 12h8M8 16h8M8 8h8" stroke="#dc2626" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div>
                    <h2>RSS Feeds</h2>
                    <p>Configure financial news and market update feeds</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                <div className="rss-feeds-list">
                  <h3>Current RSS Feeds ({rssFeeds.length})</h3>
                  {rssFeeds.map(feed => (
                    <div key={feed.id} className="rss-feed-item">
                      <div className="feed-controls">
                        <input
                          type="checkbox"
                          checked={feed.enabled}
                          onChange={() => handleToggleFeed(feed.id)}
                          className="feed-checkbox"
                        />
                        <input
                          type="text"
                          value={feed.name}
                          onChange={(e) => handleEditFeed(feed.id, 'name', e.target.value)}
                          className="feed-name-input"
                        />
                        <input
                          type="url"
                          value={feed.url}
                          onChange={(e) => handleEditFeed(feed.id, 'url', e.target.value)}
                          className="feed-url-input"
                          placeholder="RSS Feed URL"
                        />
                        <div className="feed-color-picker">
                          <label>Color:</label>
                          <input
                            type="color"
                            value={feed.color || '#dc2626'}
                            onChange={(e) => handleColorChange(feed.id, e.target.value)}
                            className="color-input"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveFeed(feed.id)}
                          className="remove-feed-button"
                          title="Remove this RSS feed"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="add-feed-section">
                  <h3>Add New RSS Feed</h3>
                  <div className="add-feed-form">
                    <input
                      type="text"
                      value={newFeedName}
                      onChange={(e) => setNewFeedName(e.target.value)}
                      placeholder="Feed Name (e.g., CNN Business)"
                      className="setting-input"
                    />
                    <input
                      type="url"
                      value={newFeedUrl}
                      onChange={(e) => setNewFeedUrl(e.target.value)}
                      placeholder="RSS Feed URL"
                      className="setting-input"
                    />
                    <button
                      onClick={handleAddFeed}
                      className="add-feed-button"
                      disabled={!newFeedName.trim() || !newFeedUrl.trim()}
                    >
                      Add RSS Feed
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        if (activeModule === 'social') {
          return (
            <div className="settings-tab-content">
              <div className="module-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveModule(null)}
                >
                  ← Back to Feeds
                </button>
                <div className="module-title">
                  <div className="module-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h2>Social Media Feeds</h2>
                    <p>Configure social media monitoring and feeds</p>
                  </div>
                </div>
              </div>
              
              <div className="module-settings">
                {/* Platform Selection */}
                <div className="platform-tabs">
                  <button 
                    className={`platform-tab ${activeSocialPlatform === 'reddit' ? 'active' : ''}`}
                    onClick={() => setActiveSocialPlatform('reddit')}
                  >
                    <span className="platform-icon">🔴</span>
                    Reddit
                  </button>
                  <button 
                    className={`platform-tab ${activeSocialPlatform === 'twitter' ? 'active' : ''}`}
                    onClick={() => setActiveSocialPlatform('twitter')}
                    disabled
                  >
                    <span className="platform-icon">𝕏</span>
                    X (Twitter)
                  </button>
                  <button 
                    className={`platform-tab ${activeSocialPlatform === 'linkedin' ? 'active' : ''}`}
                    onClick={() => setActiveSocialPlatform('linkedin')}
                    disabled
                  >
                    <span className="platform-icon">💼</span>
                    LinkedIn
                  </button>
                  <button 
                    className={`platform-tab ${activeSocialPlatform === 'youtube' ? 'active' : ''}`}
                    onClick={() => setActiveSocialPlatform('youtube')}
                    disabled
                  >
                    <span className="platform-icon">📺</span>
                    YouTube
                  </button>
                </div>

                {/* Reddit Configuration */}
                {activeSocialPlatform === 'reddit' && (
                  <div className="setting-section">
                    <h3>Reddit Configuration</h3>
                  
                  {!userApiKeys?.redditEnabled ? (
                    <div className="integration-notice">
                      <p>📝 Reddit integration is not enabled. Go to <strong>Integrations & Modules → Reddit</strong> to enable it first.</p>
                    </div>
                  ) : (
                    <>
                      <div className="setting-item">
                        <h4>Search Terms</h4>
                        <div className="search-terms-input">
                          <input
                            type="text"
                            placeholder="Enter search terms (e.g., AAPL, Tesla, investing tips)"
                            className="setting-input"
                          />
                          <button className="add-term-button">Add Term</button>
                        </div>
                        <div className="search-terms-list">
                          <span className="search-term-tag">AAPL <button>×</button></span>
                          <span className="search-term-tag">Tesla <button>×</button></span>
                          <span className="search-term-tag">dividend stocks <button>×</button></span>
                        </div>
                      </div>

                      <div className="setting-item">
                        <h4>Subreddits to Monitor</h4>
                        <div className="subreddit-input">
                          <input
                            type="text"
                            placeholder="Enter subreddit name (without r/)"
                            className="setting-input"
                          />
                          <button className="add-subreddit-button">Add Subreddit</button>
                        </div>
                        <div className="subreddit-list">
                          <div className="subreddit-item">
                            <span className="subreddit-name">r/stocks</span>
                            <label className="toggle-switch">
                              <input type="checkbox" checked />
                              <span className="toggle-slider"></span>
                            </label>
                            <button className="remove-subreddit">×</button>
                          </div>
                          <div className="subreddit-item">
                            <span className="subreddit-name">r/investing</span>
                            <label className="toggle-switch">
                              <input type="checkbox" checked />
                              <span className="toggle-slider"></span>
                            </label>
                            <button className="remove-subreddit">×</button>
                          </div>
                        </div>
                      </div>

                      <div className="setting-item">
                        <h4>Filter Options</h4>
                        <div className="filter-options">
                          <div className="filter-group">
                            <label>Sort By:</label>
                            <select className="setting-select">
                              <option value="hot">Hot</option>
                              <option value="new">New</option>
                              <option value="top">Top</option>
                              <option value="rising">Rising</option>
                            </select>
                          </div>
                          <div className="filter-group">
                            <label>Time Period:</label>
                            <select className="setting-select">
                              <option value="hour">Past Hour</option>
                              <option value="day">Past Day</option>
                              <option value="week">Past Week</option>
                              <option value="month">Past Month</option>
                            </select>
                          </div>
                          <div className="filter-group">
                            <label>Posts per Source:</label>
                            <input type="number" min="5" max="50" value="25" className="setting-input-small" />
                          </div>
                        </div>
                      </div>

                      <div className="setting-item">
                        <h4>Content Filters</h4>
                        <div className="content-filters">
                          <label>
                            <input type="checkbox" /> Hide NSFW content
                          </label>
                          <label>
                            <input type="checkbox" /> Hide spoilers
                          </label>
                          <label>
                            <input type="checkbox" /> Hide posts with low scores (&lt; 10)
                          </label>
                          <label>
                            <input type="checkbox" checked /> Enable stock detection
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  </div>
                )}

                {/* X (Twitter) Configuration */}
                {activeSocialPlatform === 'twitter' && (
                  <div className="setting-section">
                    <h3>X (Twitter) Configuration</h3>
                    <div className="coming-soon-notice">
                      <h4>🚧 Coming Soon</h4>
                      <p>X (Twitter) integration is currently in development. This will include:</p>
                      <ul>
                        <li>Monitor financial accounts and hashtags</li>
                        <li>Real-time tweet analysis</li>
                        <li>Sentiment tracking for stocks</li>
                        <li>Influencer monitoring</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* LinkedIn Configuration */}
                {activeSocialPlatform === 'linkedin' && (
                  <div className="setting-section">
                    <h3>LinkedIn Configuration</h3>
                    <div className="coming-soon-notice">
                      <h4>🚧 Coming Soon</h4>
                      <p>LinkedIn integration is currently in development. This will include:</p>
                      <ul>
                        <li>Business and finance content monitoring</li>
                        <li>Company page updates</li>
                        <li>Industry news tracking</li>
                        <li>Professional insights</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* YouTube Configuration */}
                {activeSocialPlatform === 'youtube' && (
                  <div className="setting-section">
                    <h3>YouTube Configuration</h3>
                    <div className="coming-soon-notice">
                      <h4>🚧 Coming Soon</h4>
                      <p>YouTube integration is currently in development. This will include:</p>
                      <ul>
                        <li>Financial channel monitoring</li>
                        <li>Video transcript analysis</li>
                        <li>Market commentary tracking</li>
                        <li>Educational content discovery</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="settings-tab-content">
            <h2>Feed Management</h2>
            <p className="settings-intro">Configure and manage your RSS feeds and social media sources</p>
            
            <div className="modules-grid">
              
              {/* RSS Feeds Module */}
              <div 
                className="module-card clickable"
                onClick={() => setActiveModule('rss')}
              >
                <div className="module-header">
                  <div className="module-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#dc2626" strokeWidth="2" fill="none"/>
                      <path d="M8 12h8M8 16h8M8 8h8" stroke="#dc2626" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="module-info">
                    <h3>RSS Feeds</h3>
                    <p>Financial news and market updates</p>
                    <span className="module-status active">{rssFeeds.length} feeds configured</span>
                  </div>
                </div>
                <div className="module-arrow">→</div>
              </div>

              {/* Social Feeds Module */}
              <div 
                className="module-card clickable"
                onClick={() => setActiveModule('social')}
              >
                <div className="module-header">
                  <div className="module-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div className="module-info">
                    <h3>Social Media Feeds</h3>
                    <p>Reddit, X, LinkedIn, and YouTube</p>
                    <span className={`module-status ${userApiKeys?.redditEnabled === true ? 'enabled' : 'available'}`}>
                      {userApiKeys?.redditEnabled === true ? 'Reddit Active' : 'Available'}
                    </span>
                  </div>
                </div>
                <div className="module-arrow">→</div>
              </div>

            </div>
          </div>
        );


      case 'other-settings':
        return (
          <div className="settings-tab-content">
            <h2>Other Settings</h2>
            
            <div className="setting-item">
              <label htmlFor="refresh-interval">RSS Feed Refresh Interval (seconds)</label>
              <div className="setting-control">
                <input
                  type="number"
                  id="refresh-interval"
                  min="10"
                  max="300"
                  value={refreshInterval}
                  onChange={handleRefreshIntervalChange}
                  className="setting-input"
                />
                <span className="setting-unit">seconds</span>
              </div>
              <p className="setting-description">
                How often to refresh all RSS feeds (10-300 seconds)
              </p>
            </div>

            <div className="setting-item">
              <h3>Reset to Defaults</h3>
              <p className="setting-description">
                Clear all custom prompts and feeds, and restore the default ones. This will help resolve any duplicate issues.
              </p>
              <button 
                className="setting-button danger"
                onClick={handleResetToDefaults}
              >
                🔄 Reset Prompts & Feeds to Defaults
              </button>
            </div>

            <div className="about-info">
              <h3>About BullBearly</h3>
              <p><strong>Version:</strong> 0.1.0</p>
              <p><strong>Description:</strong> Advanced stock market aggregation platform with AI analysis</p>
              <p><strong>Features:</strong> Real-time RSS feeds, AI-powered analysis, multiple data sources, customizable refresh rates</p>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="settings-tab-content">
            <Profile />
          </div>
        );

      case 'admin':
        return (
          <div className="settings-tab-content">
            <Admin />
          </div>
        );

      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="page-content">
      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Configure your BullBearly experience</p>
        </div>
        
        <div className="settings-layout">
          <div className="settings-sidebar">
            <div className="settings-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="settings-main">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;